import { useState, useEffect } from 'react';

export const useTracking = (socket, user) => {
  const [activeTracking, setActiveTracking] = useState(null);
  const [handymanLocation, setHandymanLocation] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [handymanEta, setHandymanEta] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => {
    if (!socket) return;

    const handleRequestAccepted = (data) => {
      console.log('🎉 Your service request was accepted!', data);
    };

    const handleHandymanLocationUpdate = (locationData) => {
      console.log('📍 Handyman location update:', locationData);
      setHandymanLocation(locationData);
    };

    const handleJobStatusUpdate = (statusData) => {
      console.log('🔄 Job status update:', statusData);
      setJobStatus(statusData);

      const roleName = user?.role?.name || user?.role;
      if (statusData.status === 'assigned' && roleName === 'handyman') {
        setActiveJob((prev) => {
          if (!prev || prev.serviceRequestId === statusData.serviceRequestId) {
            return { ...prev, ...statusData };
          }
          return prev;
        });
      }
    };

    const handleHandymanEtaUpdate = (etaData) => {
      console.log('⏱️ Handyman ETA update:', etaData);
      setHandymanEta(etaData);
    };

    const handleCustomerAcceptOffer = (data) => {
      console.log('✅ Customer accepted offer:', data);
      setActiveJob(data);
    };

    const handleTaskCompleted = (data) => {
      console.log('✅ Task completed by customer:', data);
      setActiveJob(null);
    };

    const handleTaskCompletedAlt = (data) => {
      console.log('✅ Task completed (alternative event):', data);
      setActiveJob(null);
    };

    const handleJobCompleted = (data) => {
      console.log('✅ Job completed:', data);
      setActiveJob(null);
    };

    socket.on('request:accepted', handleRequestAccepted);
    socket.on('handyman:location:update', handleHandymanLocationUpdate);
    socket.on('job:status:update', handleJobStatusUpdate);
    socket.on('handyman:eta:update', handleHandymanEtaUpdate);
    socket.on('customer:accept-offer', handleCustomerAcceptOffer);
    socket.on('taskCompleted', handleTaskCompleted);
    socket.on('task:completed', handleTaskCompletedAlt);
    socket.on('job:completed', handleJobCompleted);

    return () => {
      socket.off('request:accepted', handleRequestAccepted);
      socket.off('handyman:location:update', handleHandymanLocationUpdate);
      socket.off('job:status:update', handleJobStatusUpdate);
      socket.off('handyman:eta:update', handleHandymanEtaUpdate);
      socket.off('customer:accept-offer', handleCustomerAcceptOffer);
      socket.off('taskCompleted', handleTaskCompleted);
      socket.off('task:completed', handleTaskCompletedAlt);
      socket.off('job:completed', handleJobCompleted);
    };
  }, [socket, user]);

  const acceptHandymanOffer = (serviceRequestId, handymanId, offerData) => {
    const roleName = user?.role?.name || user?.role;
    if (socket && roleName === 'customer') {
      socket.emit('customer:accept-offer', {
        serviceRequestId,
        handymanId,
        customerId: user._id,
        ...offerData,
      });
      console.log(`✅ Customer accepted offer from handyman: ${handymanId}`);
    }
  };

  const startTracking = (serviceRequestId) => {
    if (socket && user) {
      socket.emit('start:tracking', {
        serviceRequestId,
        customerId: user._id,
      });
    }
  };

  const joinTaskRoom = (serviceRequestId) => {
    if (socket && user) {
      socket.emit('join-task-room', {
        taskId: serviceRequestId,
        userId: user._id,
        role: user.role?.name || user.role,
      });
    }
  };

  const leaveTaskRoom = (serviceRequestId) => {
    if (socket && user) {
      socket.emit('leave-task-room', {
        taskId: serviceRequestId,
        userId: user._id,
      });
    }
  };

  return {
    activeTracking,
    setActiveTracking,
    handymanLocation,
    setHandymanLocation,
    jobStatus,
    setJobStatus,
    handymanEta,
    setHandymanEta,
    activeJob,
    setActiveJob,
    acceptHandymanOffer,
    startTracking,
    joinTaskRoom,
    leaveTaskRoom,
  };
};
